import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Droplet, AlertTriangle, Phone, MapPin, User, Heart } from "lucide-react";

const GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];

export default function BloodPage() {
  const [tab, setTab] = useState("donors");
  const [donors, setDonors] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState({ blood_group: "", area: "" });

  const [donorForm, setDonorForm] = useState({ name: "", blood_group: "B+", phone: "", area: "", age: "", last_donation: "" });
  const [reqForm, setReqForm] = useState({ patient_name: "", blood_group: "B+", hospital: "", area: "", contact_name: "", phone: "", urgency: "urgent", notes: "" });

  const load = () => {
    const p = {};
    if (filter.blood_group) p.blood_group = filter.blood_group;
    if (filter.area) p.area = filter.area;
    api.get("/blood-donors", { params: p }).then((r) => setDonors(r.data)).catch(() => {});
    api.get("/blood-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  const registerDonor = async () => {
    try {
      await api.post("/blood-donors", { ...donorForm, age: donorForm.age ? Number(donorForm.age) : null });
      toast.success("धन्यवाद! आप डोनर सूची में जुड़ गये");
      setDonorForm({ name: "", blood_group: "B+", phone: "", area: "", age: "", last_donation: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const postRequest = async () => {
    try {
      await api.post("/blood-requests", reqForm);
      toast.success("अनुरोध पोस्ट हुआ — डोनर्स संपर्क करेंगे");
      setReqForm({ patient_name: "", blood_group: "B+", hospital: "", area: "", contact_name: "", phone: "", urgency: "urgent", notes: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <>
      <PublicHeader />
      <section className="bg-gradient-to-br from-red-700 via-red-800 to-red-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <div className="flex items-center gap-3 mb-3">
            <Droplet className="w-6 h-6 text-white fill-white"/>
            <span className="uppercase tracking-[0.28em] text-[11px] text-red-200 font-black">Blood Donor Network · रक्तदान</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl md:text-5xl font-black">एक बूंद <span className="text-amber-300">जीवन बचाती है</span></h1>
          <p className="text-red-100 mt-3 max-w-2xl">INDIAN-NEWS-21 का रक्तदाता नेटवर्क — डोनर बनें या ज़रूरत में सहायता मांगें।</p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-8" data-testid="blood-page">
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[["donors", "🩸 डोनर सूची"], ["register", "✍️ डोनर बनें"], ["request", "🚨 खून चाहिए"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === k ? "border-red-600 text-red-700" : "border-transparent text-slate-500"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Active requests strip */}
        {requests.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2 text-red-800"><AlertTriangle className="w-4 h-4"/><b>{requests.length} सक्रिय अनुरोध</b></div>
            <div className="space-y-2">
              {requests.slice(0, 3).map((r) => (
                <div key={r.id} className="text-sm flex justify-between items-center flex-wrap gap-2" data-testid={`req-${r.id}`}>
                  <div>
                    <span className="inline-block bg-red-600 text-white font-bold text-xs px-2 py-0.5 rounded mr-2">{r.blood_group}</span>
                    <b>{r.patient_name}</b> · {r.hospital}, {r.area}
                  </div>
                  <a href={`tel:${r.phone}`} className="text-red-700 font-bold hover:underline"><Phone className="w-3.5 h-3.5 inline mr-1"/>{r.phone}</a>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "donors" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <select value={filter.blood_group} onChange={(e) => setFilter({ ...filter, blood_group: e.target.value })} className="h-10 px-3 rounded-md border border-slate-200" data-testid="filter-group">
                <option value="">सभी ब्लड ग्रुप</option>
                {GROUPS.map((g) => <option key={g}>{g}</option>)}
              </select>
              <Input placeholder="क्षेत्र / शहर" value={filter.area} onChange={(e) => setFilter({ ...filter, area: e.target.value })} data-testid="filter-area"/>
              <div className="text-sm text-slate-500 flex items-center">{donors.length} डोनर मिले</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {donors.map((d, i) => (
                <div key={`${d.phone}-${i}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3" data-testid={`donor-${i}`}>
                  <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-black text-lg">{d.blood_group}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F172A] truncate flex items-center gap-1"><User className="w-3.5 h-3.5"/>{d.name}</div>
                    {d.area && <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3"/>{d.area}</div>}
                    <a href={`tel:${d.phone}`} className="text-sm text-red-700 font-bold hover:underline flex items-center gap-1"><Phone className="w-3 h-3"/>{d.phone}</a>
                  </div>
                </div>
              ))}
              {donors.length === 0 && <div className="col-span-full text-slate-500 italic text-center py-8">कोई डोनर नहीं मिला — डोनर बनकर पहले बनें!</div>}
            </div>
          </>
        )}

        {tab === "register" && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl">
            <h3 className="font-bold text-lg text-[#0F172A] mb-4 flex items-center gap-2"><Heart className="w-5 h-5 text-red-600 fill-red-100"/>डोनर के रूप में पंजीकरण</h3>
            <div className="space-y-3">
              <Input placeholder="आपका नाम *" value={donorForm.name} onChange={(e) => setDonorForm({ ...donorForm, name: e.target.value })} data-testid="donor-name"/>
              <div className="grid grid-cols-2 gap-3">
                <select value={donorForm.blood_group} onChange={(e) => setDonorForm({ ...donorForm, blood_group: e.target.value })} className="h-10 px-3 rounded-md border border-slate-200" data-testid="donor-group">
                  {GROUPS.map((g) => <option key={g}>{g}</option>)}
                </select>
                <Input placeholder="फ़ोन *" value={donorForm.phone} onChange={(e) => setDonorForm({ ...donorForm, phone: e.target.value })} data-testid="donor-phone"/>
              </div>
              <Input placeholder="क्षेत्र / शहर *" value={donorForm.area} onChange={(e) => setDonorForm({ ...donorForm, area: e.target.value })} data-testid="donor-area"/>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="उम्र" value={donorForm.age} onChange={(e) => setDonorForm({ ...donorForm, age: e.target.value })}/>
                <Input type="date" placeholder="अंतिम रक्तदान" value={donorForm.last_donation} onChange={(e) => setDonorForm({ ...donorForm, last_donation: e.target.value })}/>
              </div>
              <Button onClick={registerDonor} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="register-donor"><Heart className="w-4 h-4 mr-2 fill-white"/>डोनर बनें</Button>
            </div>
          </div>
        )}

        {tab === "request" && (
          <div className="bg-white border-2 border-red-300 rounded-lg p-6 max-w-2xl">
            <h3 className="font-bold text-lg text-red-800 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5"/>तुरंत रक्त की आवश्यकता</h3>
            <div className="space-y-3">
              <Input placeholder="रोगी का नाम *" value={reqForm.patient_name} onChange={(e) => setReqForm({ ...reqForm, patient_name: e.target.value })} data-testid="req-name"/>
              <div className="grid grid-cols-2 gap-3">
                <select value={reqForm.blood_group} onChange={(e) => setReqForm({ ...reqForm, blood_group: e.target.value })} className="h-10 px-3 rounded-md border border-slate-200" data-testid="req-group">
                  {GROUPS.map((g) => <option key={g}>{g}</option>)}
                </select>
                <select value={reqForm.urgency} onChange={(e) => setReqForm({ ...reqForm, urgency: e.target.value })} className="h-10 px-3 rounded-md border border-slate-200">
                  <option value="urgent">तुरंत</option><option value="critical">अत्यंत गंभीर</option>
                </select>
              </div>
              <Input placeholder="अस्पताल *" value={reqForm.hospital} onChange={(e) => setReqForm({ ...reqForm, hospital: e.target.value })} data-testid="req-hospital"/>
              <Input placeholder="क्षेत्र / शहर *" value={reqForm.area} onChange={(e) => setReqForm({ ...reqForm, area: e.target.value })} data-testid="req-area"/>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="संपर्क नाम *" value={reqForm.contact_name} onChange={(e) => setReqForm({ ...reqForm, contact_name: e.target.value })}/>
                <Input placeholder="फ़ोन *" value={reqForm.phone} onChange={(e) => setReqForm({ ...reqForm, phone: e.target.value })}/>
              </div>
              <Textarea rows={2} placeholder="अतिरिक्त जानकारी" value={reqForm.notes} onChange={(e) => setReqForm({ ...reqForm, notes: e.target.value })}/>
              <Button onClick={postRequest} className="w-full bg-red-700 hover:bg-red-800 text-white" data-testid="post-request"><AlertTriangle className="w-4 h-4 mr-2"/>अनुरोध पोस्ट करें</Button>
            </div>
          </div>
        )}
      </main>
      <PublicFooter />
    </>
  );
}
